import axios from "axios";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_API_URL || "/api/v1";

const client = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("edupulse_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshPromise = null;

function getErrorMessage(error) {
  const data = error.response?.data;
  if (data?.error) return data.error;
  if (data?.message) return data.message;
  if (error.code === "ECONNABORTED") return "Request timed out. Please try again.";
  if (!error.response) return "Network error. Please check your connection.";
  const status = error.response?.status;
  if (status === 403) return "You don't have permission for this action.";
  if (status === 404) return "Requested resource not found.";
  if (status === 429) return "Too many requests. Please wait and try again.";
  if (status >= 500) return "Server error. Please try again later.";
  return "Something went wrong. Please try again.";
}

let _muted = false;

client.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    if (
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes("/auth/")
    ) {
      original._retry = true;
      const refreshToken = localStorage.getItem("edupulse_refresh");

      if (!refreshToken) {
        window.dispatchEvent(new Event("edupulse:logout"));
        return Promise.reject(error);
      }

      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${API_BASE}/auth/refresh-token`, { refreshToken })
            .finally(() => { refreshPromise = null; });
        }
        const { data } = await refreshPromise;
        const newToken = data.accessToken || data.token;
        if (newToken) localStorage.setItem("edupulse_token", newToken);
        if (data.refreshToken) localStorage.setItem("edupulse_refresh", data.refreshToken);
        original.headers.Authorization = `Bearer ${newToken}`;
        return client(original);
      } catch {
        window.dispatchEvent(new Event("edupulse:logout"));
        return Promise.reject(error);
      }
    }

    const skipToast = original?._silent
      || _muted
      || error.response?.status === 401
      || original?.url?.includes("/auth/");

    if (!skipToast) {
      toast.error(getErrorMessage(error));
    }

    return Promise.reject(error);
  }
);

export function muteToasts(muted) { _muted = muted; }
export function isToastMuted() { return _muted; }

export function saveTokens(token, refresh) {
  localStorage.setItem("edupulse_token", token);
  if (refresh) localStorage.setItem("edupulse_refresh", refresh);
}

export function clearTokens() {
  localStorage.removeItem("edupulse_token");
  localStorage.removeItem("edupulse_refresh");
  localStorage.removeItem("edupulse_user");
}

export function getSavedUser() {
  try {
    return JSON.parse(localStorage.getItem("edupulse_user"));
  } catch {
    return null;
  }
}

export function saveUser(user) {
  localStorage.setItem("edupulse_user", JSON.stringify(user));
}

export default client;
